import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PersonModal from './PersonModal';
import ReplacePersonModal from './ReplacePersonModal';
import DepartmentsTab from './DepartmentsTab';
import ContractsTab from './ContractsTab';
import ClientsTab from './ClientsTab';
import StaffAccessTab from './StaffAccessTab';

const API = process.env.REACT_APP_API_URL || '';

const USER_TYPE_LABELS = {
  platform_superuser:  { label: 'Platform Superuser',  color: 'purple', priority: 1 },
  org_superadmin:      { label: 'Org Superadmin',      color: 'blue',   priority: 2 },
  compliance_officer:  { label: 'Compliance Officer',  color: 'emerald', priority: 3 },
  compliance_analyst:  { label: 'Compliance Analyst',  color: 'teal',   priority: 4 },
  department_lead:     { label: 'Department Lead',     color: 'amber',  priority: 5 },
  department_user:     { label: 'Department User',     color: 'slate',  priority: 6 },
};

/**
 * People page — Stage 5 of the user-management revamp.
 *
 * Replaces the old fragmented Settings tabs (RegisterTeam, RegisterSimple,
 * RegisterTeamCsv, etc.) with a single clean view.
 *
 * Access: org_superadmin (own org) or platform_superuser (any org via ?org_id).
 */
export default function People() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orgIdFromQuery = searchParams.get('org');

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Cross-org context (when platform_superuser is managing another org)
  const [contextOrgId, setContextOrgId] = useState(null);
  const [contextOrgName, setContextOrgName] = useState(null);
  const [isCrossOrg, setIsCrossOrg] = useState(false);
  // Third-party administrator (TPA) accounts: the access administrator at
  // the TPA's home workspace also sees Clients and Staff access, and may open
  // a client's Departments & people via ?org=<client id>.
  const [isTpaAdmin, setIsTpaAdmin] = useState(false);
  const [isClientContext, setIsClientContext] = useState(false);

  // Data
  const [users, setUsers] = useState([]);
  // 'local' or a federated mode. Decides whether re-sending sign-in mail is
  // offered for a user who is already active, see RowActionMenu.
  const [orgIdentityMode, setOrgIdentityMode] = useState('local');
  const [departments, setDepartments] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);

  // Filters
  const [filterType, setFilterType] = useState('all');
  const [filterDept, setFilterDept] = useState('all');
  const [filterStatus, setFilterStatus] = useState('active');
  const [searchQuery, setSearchQuery] = useState('');

  // Tabs — People (default) vs Departments. Same auth surface; just swaps the
  // body so org_superadmins manage everything from one screen.
  const [tab, setTab] = useState('people');

  // Modals
  const [editingPerson, setEditingPerson] = useState(null); // null = closed, {} = add new, {id, ...} = edit
  const [replacingPerson, setReplacingPerson] = useState(null);

  // Backfill state
  const [backfilling, setBackfilling] = useState(false);

  // Toast
  const [toast, setToast] = useState(null);

  // ─── Session check ───
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/auth/session`, { credentials: 'include' });
        const data = await res.json();
        if (!data.success) {
          navigate('/login');
          return;
        }
        const u = data.user;
        // Allowed: platform superuser, org superadmin, compliance officer
        // (org-wide admin role added 2026-05-17). Legacy role fallbacks
        // mirror what the server's requireOrgManageAccess accepts.
        const ut = u.user_type || u.effective_user_type || null;
        const isAllowed =
          ut === 'platform_superuser' ||
          ut === 'org_superadmin' ||
          ut === 'compliance_officer' ||
          u.role === 'superadmin' ||
          u.role === 'superuser' ||
          (!ut && u.role === 'admin' && !u.department_id);
        if (!isAllowed) {
          navigate('/');
          return;
        }

        // Determine which org's people we are managing.
        // - If ?org=<id> is present AND the actor is platform_superuser, use that.
        // - Otherwise default to the actor's own org.
        const isPlatform =
          u.user_type === 'platform_superuser' ||
          u.effective_user_type === 'platform_superuser' ||
          u.role === 'superadmin';

        let targetOrgId = u.organization_id;
        let targetOrgName = u.organization_name;
        let crossOrg = false;
        let clientContext = false;

        const tpaAdmin =
          u.workspace?.account_kind === 'tpa' &&
          !u.identity_user_id &&
          (ut ? ut === 'org_superadmin' : u.role === 'superuser');

        if (orgIdFromQuery && (isPlatform || tpaAdmin) && orgIdFromQuery !== u.organization_id) {
          // The server decides whether this actor may manage that organization
          // (platform support: any; TPA administrator: only its own clients).
          try {
            const orgRes = await fetch(`${API}/api/admin/v2/orgs/${orgIdFromQuery}`, { credentials: 'include' });
            const orgData = await orgRes.json();
            if (orgData.success) {
              targetOrgId = orgIdFromQuery;
              targetOrgName = orgData.organization?.name || 'Unknown Org';
              crossOrg = isPlatform;
              clientContext = !isPlatform && orgData.organization?.account_kind === 'tpa_client';
            }
          } catch { /* keep default */ }
        }

        setUser(u);
        setIsTpaAdmin(tpaAdmin);
        setContextOrgId(targetOrgId);
        setContextOrgName(targetOrgName);
        setIsCrossOrg(crossOrg);
        setIsClientContext(clientContext);
      } catch (err) {
        navigate('/login');
      }
      setLoading(false);
    })();
  }, [navigate, orgIdFromQuery]);

  // ─── Load users + departments ───
  const fetchData = useCallback(async () => {
    if (!contextOrgId) return;
    setUsersLoading(true);
    try {
      const [usersRes, deptsRes] = await Promise.all([
        fetch(`${API}/api/admin/v2/orgs/${contextOrgId}/users`, { credentials: 'include' }),
        fetch(`${API}/api/admin/v2/orgs/${contextOrgId}/departments`, { credentials: 'include' }),
      ]);
      const usersData = await usersRes.json();
      const deptsData = await deptsRes.json();
      if (usersData.success) {
        setUsers(usersData.users || []);
        setOrgIdentityMode(usersData.identity_mode || 'local');
      }
      if (deptsData.success) setDepartments(deptsData.departments || []);
    } catch (err) {
      console.error('Failed to load people:', err);
    }
    setUsersLoading(false);
  }, [contextOrgId]);

  useEffect(() => { if (contextOrgId) fetchData(); }, [contextOrgId, fetchData]);

  // ─── Toast auto-dismiss ───
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 7000);
    return () => clearTimeout(t);
  }, [toast]);

  const showToast = (type, title, body) => setToast({ type, title, body });

  // ─── Filtering ───
  const filteredUsers = users.filter(u => {
    if (filterType !== 'all' && u.user_type !== filterType) return false;
    if (filterDept !== 'all' && u.department_id !== filterDept) return false;
    if (filterStatus !== 'all' && u.status !== filterStatus) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!(u.name || '').toLowerCase().includes(q) && !(u.email || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });

  // ─── Action handlers ───
  // `result` is the full create/update response. Saving the person and getting
  // them into Mandate are two different things, and for a long time the second
  // could fail silently while this screen reported success, which is how users
  // went missing from Mandate without anyone noticing. If the push did not
  // land, say so here.
  const handlePersonSaved = (action, savedUser, result = null) => {
    setEditingPerson(null);
    fetchData();

    if (action !== 'create') {
      showToast('success', `${savedUser.name} updated`, '');
      return;
    }

    // The "did NOT reach Mandate" branch was removed 2026-08-12. It reported a
    // failed HTTP push into the standalone Mandate app's own user table; that
    // app is shut down and Precision Mandate reads Core's users directly, so a
    // saved user has already arrived. Keeping the check would have shown a red
    // error on every single person added.
    showToast('success', `${savedUser.name} added`, savedUser.email + ' will receive a welcome email');
  };

  const handleDeactivate = async (target) => {
    if (!window.confirm(`Deactivate ${target.name}? They will no longer be able to log in. You can reactivate them later from this page.`)) return;
    // Second, explicit choice: keep the email reserved (normal offboarding,
    // the person may be reactivated) or release it so the address can be
    // registered again (retire-and-reinvite). Releasing also unlinks the
    // Microsoft identity server-side so a fresh registration for the same
    // person is not shadowed by the retired row.
    const releaseEmail = window.confirm(
      `Also release ${target.email}?\n\n` +
      `OK — release: the email address (and Microsoft sign-in link, if any) is freed so this person can be invited again as a brand-new account. The old record is kept for audit but cannot be reactivated onto this address.\n\n` +
      `Cancel — keep: the address stays reserved for this account so you can simply reactivate it later.`
    );
    try {
      const res = await fetch(`${API}/api/admin/v2/users/${target.id}/deactivate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reason: releaseEmail ? 'Deactivated via People page (email released for re-registration)' : 'Deactivated via People page',
          release_email: releaseEmail,
        }),
      });
      const data = await res.json();
      if (data.success) {
        showToast('success', `${target.name} deactivated`,
          data.email_released ? `${target.email} released — you can now re-invite them` : 'They can no longer access the platform');
        fetchData();
      } else {
        showToast('error', 'Could not deactivate', data.message);
      }
    } catch (err) {
      showToast('error', 'Network error', err.message);
    }
  };

  const handleReactivate = async (target) => {
    try {
      const res = await fetch(`${API}/api/admin/v2/users/${target.id}/reactivate`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (data.success) {
        showToast('success', `${target.name} reactivated`, '');
        fetchData();
      }
    } catch (err) {
      showToast('error', 'Network error', err.message);
    }
  };

  const handleResendInvite = async (target) => {
    try {
      const res = await fetch(`${API}/api/admin/v2/users/${target.id}/send-invite`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (data.success) {
        showToast('success', `Invite resent`, `New welcome email sent to ${target.email}`);
      } else {
        showToast('error', 'Could not send invite', data.message);
      }
    } catch (err) {
      showToast('error', 'Network error', err.message);
    }
  };

  const handleReplaceComplete = (result) => {
    setReplacingPerson(null);
    showToast(
      'success',
      `${result.leaver.email} replaced`,
      `${result.new_user.email} created and will receive a welcome email`
    );
    fetchData();
  };

  const handleBackfillAppRoles = async () => {
    if (!contextOrgId) return;
    if (!window.confirm(
      `Re-grant app access for all active users in ${contextOrgName}?\n\n` +
      `This will re-run the role bridge for every active user, ensuring they have ` +
      `roles for all currently-purchased products. Use this after activating a new ` +
      `product on the org so existing users get access to it.\n\n` +
      `Existing role grants will be replaced based on each user's user_type.`
    )) return;

    setBackfilling(true);
    try {
      const res = await fetch(`${API}/api/admin/v2/orgs/${contextOrgId}/users/backfill-app-roles`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();

      if (data.total === undefined) {
        showToast('error', 'Backfill failed', data.message || 'Unknown error');
        setBackfilling(false);
        return;
      }

      // Retired 2026-08-12: this used to report how many users reached the
      // standalone Mandate app over HTTP. That app is shut down and access is
      // now carried by the user_app_roles rows this route writes, so the grant
      // itself is the outcome worth reporting.
      const mapped = data.department_mapping?.mapped || 0;
      const mappingNote = mapped > 0
        ? `Mapped ${mapped} department(s) to the contract first. `
        : (data.department_mapping?.skipped_reason
            ? `Departments not auto-mapped (${data.department_mapping.skipped_reason}). `
            : '');

      const failed = (data.errors || []).length;
      if (failed > 0) {
        const names = (data.errors || []).slice(0, 5)
          .map((u) => `${u.email} (${u.error})`).join('; ');
        showToast(
          'error',
          `${failed} of ${data.total} users could not be updated`,
          `${mappingNote}${data.updated} updated. Failed: ${names}`
          + (failed > 5 ? ` and ${failed - 5} more.` : '')
        );
      } else {
        showToast(
          'success',
          `Access re-granted for all ${data.total} users`,
          `${mappingNote}${data.roles_granted} role grants written across every purchased product.`
        );
      }
      fetchData();
    } catch (err) {
      showToast('error', 'Network error', err.message);
    }
    setBackfilling(false);
  };

  if (loading) return <FullPageSpinner label="Loading…" />;
  if (!user) return null;

  // Stats
  const stats = {
    total: users.filter(u => u.status === 'active').length,
    superadmin: users.filter(u => u.user_type === 'org_superadmin' && u.status === 'active').length,
    compliance: users.filter(u => ['compliance_officer', 'compliance_analyst'].includes(u.user_type) && u.status === 'active').length,
    departmentLead: users.filter(u => u.user_type === 'department_lead' && u.status === 'active').length,
    departmentUser: users.filter(u => u.user_type === 'department_user' && u.status === 'active').length,
    inactive: users.filter(u => u.status !== 'active').length,
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
      {/* Header */}
      <header className="border-b border-slate-700/50 bg-slate-900/60 backdrop-blur-xl sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center shadow-lg">
              <span className="text-white font-extrabold text-[10px] tracking-[0.15em]">PCG</span>
            </div>
            <div>
              <h1 className="text-xl font-bold">People · {contextOrgName || user.organization_name}</h1>
              <p className="text-xs text-slate-500">Add, edit, replace, and deactivate team members</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {/* The super user's home IS this page — offer their other two
                surfaces instead of a dead "Back to App" link. */}
            {isClientContext ? (
              <button onClick={() => navigate('/people')} className="text-xs text-slate-400 hover:text-white transition">← Back to your clients</button>
            ) : ((user.user_type || user.effective_user_type) ? (user.user_type || user.effective_user_type) === 'org_superadmin' : user.role === 'superuser') && !isCrossOrg ? (
              <>
                <button onClick={() => navigate('/onboarding')} className="text-xs text-slate-400 hover:text-white transition">Organization setup</button>
                <button onClick={() => navigate('/team-access')} className="text-xs text-slate-400 hover:text-white transition">Team &amp; Access</button>
              </>
            ) : (
              <button
                onClick={() => navigate(isCrossOrg ? '/platform-admin' : '/app')}
                className="text-xs text-slate-400 hover:text-white transition"
              >
                ← {isCrossOrg ? 'Back to Platform Console' : 'Back to App'}
              </button>
            )}
          </div>
        </div>
        {/* Client workspace banner (a TPA administrator setting up one of its clients) */}
        {isClientContext && (
          <div className="bg-gradient-to-r from-sky-500/15 via-blue-500/15 to-sky-500/15 border-t border-sky-500/30">
            <div className="max-w-7xl mx-auto px-6 py-2 flex items-center gap-3">
              <span className="text-xs font-bold uppercase tracking-wide text-sky-300">Client workspace</span>
              <span className="text-xs text-slate-300">Setting up <strong className="text-white">{contextOrgName}</strong>. Departments and people created here belong to this client only. Give your own staff access under Staff access.</span>
            </div>
          </div>
        )}
        {/* Cross-org banner (when platform_superuser is managing another org) */}
        {isCrossOrg && (
          <div className="bg-gradient-to-r from-amber-500/15 via-orange-500/15 to-amber-500/15 border-t border-amber-500/30">
            <div className="max-w-7xl mx-auto px-6 py-2 flex items-center gap-3">
              <span className="text-xs font-bold uppercase tracking-wide text-amber-300">Platform Mode</span>
              <span className="text-xs text-slate-300">Managing <strong className="text-white">{contextOrgName}</strong> as Platform Superuser. Cross-org actions are audited.</span>
            </div>
          </div>
        )}
      </header>

      {/* Toast */}
      {toast && (
        <div className="fixed top-20 right-6 z-50 w-96">
          <div className={`rounded-xl border shadow-2xl p-4 backdrop-blur-xl ${
            toast.type === 'success'
              ? 'bg-emerald-500/15 border-emerald-500/40'
              : 'bg-red-500/15 border-red-500/40'
          }`}>
            <div className="flex items-start gap-3">
              <span className="text-2xl flex-shrink-0">{toast.type === 'success' ? '✓' : '⚠'}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white">{toast.title}</p>
                {toast.body && <p className="text-xs text-slate-300 mt-0.5">{toast.body}</p>}
              </div>
              <button onClick={() => setToast(null)} className="text-slate-400 hover:text-white text-xl leading-none">×</button>
            </div>
          </div>
        </div>
      )}

      {/* Body */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Support-access requests — the super user decides whether platform
            support may open this org's compliance apps (time-boxed, audited,
            revocable). Renders only for the org super user. */}
        {((user.user_type || user.effective_user_type) ? (user.user_type || user.effective_user_type) === 'org_superadmin' : user.role === 'superuser') && !isClientContext && (
          <SupportAccessPanel showToast={showToast} />
        )}
        {/* Tabs — People · Departments */}
        <div className="flex items-center gap-1 mb-6 border-b border-slate-700/50">
          <TabButton active={tab === 'people'} onClick={() => setTab('people')} label="People" count={users.length} />
          <TabButton active={tab === 'departments'} onClick={() => setTab('departments')} label="Departments" count={departments.length} />
          <TabButton active={tab === 'contracts'} onClick={() => setTab('contracts')} label="Contracts" />
          {isTpaAdmin && !isClientContext && (
            <>
              <TabButton active={tab === 'clients'} onClick={() => setTab('clients')} label="Clients" />
              <TabButton active={tab === 'staff_access'} onClick={() => setTab('staff_access')} label="Staff access" />
            </>
          )}
        </div>

        {tab === 'clients' && isTpaAdmin ? (
          <ClientsTab showToast={showToast} />
        ) : tab === 'staff_access' && isTpaAdmin ? (
          <StaffAccessTab showToast={showToast} />
        ) : tab === 'contracts' ? (
          <ContractsTab departments={departments} onChanged={fetchData} showToast={showToast} orgId={(isCrossOrg || isClientContext) ? contextOrgId : null} />
        ) : tab === 'departments' ? (
          <DepartmentsTab
            orgId={contextOrgId}
            departments={departments}
            onChanged={fetchData}
            showToast={showToast}
          />
        ) : (
        <>
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <StatCard label="Active" value={stats.total} />
          <StatCard label="Org Admins" value={stats.superadmin} />
          <StatCard label="Compliance" value={stats.compliance} />
          <StatCard label="Dept Leads" value={stats.departmentLead} />
          <StatCard label="Dept Users" value={stats.departmentUser} />
        </div>

        {/* Filter bar + Add button */}
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl mb-4 p-4">
          <div className="flex items-center gap-3 flex-wrap">
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search by name or email…"
              className="flex-1 min-w-[200px] bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-sm text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
            />
            <select
              value={filterType}
              onChange={e => setFilterType(e.target.value)}
              className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-blue-500 focus:outline-none"
            >
              <option value="all">All types</option>
              {Object.entries(USER_TYPE_LABELS).map(([key, def]) => (
                <option key={key} value={key}>{def.label}</option>
              ))}
            </select>
            <select
              value={filterDept}
              onChange={e => setFilterDept(e.target.value)}
              className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-blue-500 focus:outline-none"
            >
              <option value="all">All departments</option>
              {departments.map(d => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
            <select
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
              className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-blue-500 focus:outline-none"
            >
              <option value="all">All statuses</option>
              <option value="active">Active only</option>
              <option value="invited">Invited (not yet activated)</option>
              <option value="inactive">Inactive</option>
              <option value="replaced">Replaced</option>
            </select>
            <button
              onClick={handleBackfillAppRoles}
              disabled={backfilling || users.length === 0}
              title="Re-run the role bridge for every active user — use after activating a new product on this org"
              className="px-3 py-2 bg-slate-700/60 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed border border-slate-600 rounded-lg font-medium text-xs text-slate-200 transition flex-shrink-0"
            >
              {backfilling ? 'Re-granting…' : '🔄 Re-grant All Users'}
            </button>
            <button
              onClick={() => setEditingPerson({})}
              className="px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-500 rounded-lg font-medium text-sm text-white hover:shadow-lg hover:shadow-blue-500/25 transition flex-shrink-0"
            >
              + Add Person
            </button>
          </div>
        </div>

        {/* People table */}
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl overflow-hidden">
          {usersLoading ? (
            <div className="px-6 py-12 text-center text-slate-500">Loading…</div>
          ) : filteredUsers.length === 0 ? (
            <div className="px-6 py-12 text-center text-slate-500">
              {users.length === 0 ? 'No people in this organization yet.' : 'No people match your filters.'}
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-900/40 border-b border-slate-700/50">
                <tr>
                  <th className="text-left text-[11px] uppercase tracking-wide text-slate-400 font-semibold px-6 py-3">Person</th>
                  <th className="text-left text-[11px] uppercase tracking-wide text-slate-400 font-semibold px-4 py-3">Type</th>
                  <th className="text-left text-[11px] uppercase tracking-wide text-slate-400 font-semibold px-4 py-3">Department</th>
                  <th className="text-left text-[11px] uppercase tracking-wide text-slate-400 font-semibold px-4 py-3">Status</th>
                  <th className="text-left text-[11px] uppercase tracking-wide text-slate-400 font-semibold px-4 py-3">Last Login</th>
                  <th className="text-right text-[11px] uppercase tracking-wide text-slate-400 font-semibold px-6 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(p => (
                  <tr key={p.id} className="border-b border-slate-700/30 hover:bg-slate-700/20 transition">
                    <td className="px-6 py-4">
                      <div className="font-medium text-white">{p.name}</div>
                      <div className="text-xs text-slate-500">{p.email}</div>
                    </td>
                    <td className="px-4 py-4">
                      <UserTypeBadge type={p.user_type} />
                    </td>
                    <td className="px-4 py-4 text-sm text-slate-300">
                      {p.department_name || <span className="text-slate-600 italic">—</span>}
                    </td>
                    <td className="px-4 py-4">
                      <StatusBadge status={p.status} />
                    </td>
                    <td className="px-4 py-4 text-xs text-slate-500">
                      {p.last_login_at ? new Date(p.last_login_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <RowActionMenu
                        person={p}
                        currentUserId={user.id}
                        onEdit={() => setEditingPerson(p)}
                        onDeactivate={() => handleDeactivate(p)}
                        onReactivate={() => handleReactivate(p)}
                        onReplace={() => setReplacingPerson(p)}
                        onResendInvite={() => handleResendInvite(p)}
                        isFederated={orgIdentityMode !== 'local'}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        </>
        )}
      </main>

      {/* Modals */}
      {editingPerson !== null && (
        <PersonModal
          person={editingPerson}
          orgId={contextOrgId}
          departments={departments}
          actorUserType={user?.user_type || user?.effective_user_type || null}
          onClose={() => setEditingPerson(null)}
          onSaved={handlePersonSaved}
        />
      )}

      {replacingPerson && (
        <ReplacePersonModal
          person={replacingPerson}
          onClose={() => setReplacingPerson(null)}
          onComplete={handleReplaceComplete}
        />
      )}
    </div>
  );
}

// ────────────────────────── Sub-components ──────────────────────────
function TabButton({ active, onClick, label, count }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2.5 text-sm font-medium transition border-b-2 -mb-px flex items-center gap-2 ${
        active
          ? 'text-white border-blue-500'
          : 'text-slate-400 border-transparent hover:text-slate-200 hover:border-slate-600'
      }`}
    >
      {label}
      {typeof count === 'number' && (
        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
          active ? 'bg-blue-500/20 text-blue-200' : 'bg-slate-700/60 text-slate-400'
        }`}>
          {count}
        </span>
      )}
    </button>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-4">
      <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-1">{label}</div>
      <div className="text-2xl font-bold text-white">{value}</div>
    </div>
  );
}

function UserTypeBadge({ type }) {
  if (!type) return <span className="text-xs text-slate-600 italic">—</span>;
  const def = USER_TYPE_LABELS[type] || { label: type, color: 'slate' };
  const colors = {
    purple:  'bg-amber-500/15 text-amber-300 border-amber-500/30',
    blue:    'bg-blue-500/15 text-blue-300 border-blue-500/30',
    emerald: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    teal:    'bg-teal-500/15 text-teal-300 border-teal-500/30',
    amber:   'bg-amber-500/15 text-amber-300 border-amber-500/30',
    slate:   'bg-slate-500/15 text-slate-300 border-slate-500/30',
  };
  return (
    <span className={`inline-block text-[11px] font-semibold px-2 py-0.5 rounded border ${colors[def.color]}`}>
      {def.label}
    </span>
  );
}

function StatusBadge({ status }) {
  const map = {
    active:   { label: 'Active',     color: 'emerald' },
    invited:  { label: 'Invited',    color: 'blue' },
    inactive: { label: 'Inactive',   color: 'slate' },
    replaced: { label: 'Replaced',   color: 'amber' },
  };
  const def = map[status] || { label: status, color: 'slate' };
  const colors = {
    emerald: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    blue:    'bg-blue-500/10 text-blue-400 border-blue-500/20',
    slate:   'bg-slate-500/10 text-slate-400 border-slate-500/20',
    amber:   'bg-amber-500/10 text-amber-400 border-amber-500/20',
  };
  return (
    <span className={`inline-block text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full border ${colors[def.color]}`}>
      {def.label}
    </span>
  );
}

function RowActionMenu({ person, currentUserId, onEdit, onDeactivate, onReactivate, onReplace, onResendInvite, isFederated }) {
  const [open, setOpen] = useState(false);
  const isSelf = person.id === currentUserId;
  const isInactive = person.status !== 'active' && person.status !== 'invited';
  const isInvited = person.status === 'invited';
  // In a federated org the mail is only a Microsoft sign-in link. It mints no
  // token and does not touch the user's status, so it is safe to send to an
  // active user, and it is the only way to get someone their link if their row
  // was created before their access was set up. Gating this on 'invited' left
  // it unreachable for exactly those people. A local org keeps the old rule,
  // because there re-sending resets the user back to 'invited'.
  const canResend = isInvited || (isFederated && !isInactive);

  return (
    <div className="relative inline-block">
      <button
        onClick={() => setOpen(v => !v)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className="px-3 py-1.5 bg-slate-700/50 hover:bg-slate-600 rounded-lg text-xs font-medium text-white transition"
      >
        Actions ▾
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-48 bg-slate-800 border border-slate-700 rounded-lg shadow-2xl z-30 py-1">
          <MenuItem onClick={onEdit}>Edit</MenuItem>
          {canResend && (
            <MenuItem onClick={onResendInvite}>
              {isFederated ? 'Send sign-in link' : 'Resend Invite'}
            </MenuItem>
          )}
          {!isSelf && !isInactive && (
            <>
              <MenuItem onClick={onReplace} highlight>Replace Person…</MenuItem>
              <MenuItem onClick={onDeactivate} danger>Deactivate</MenuItem>
            </>
          )}
          {isInactive && person.status === 'inactive' && (
            <MenuItem onClick={onReactivate}>Reactivate</MenuItem>
          )}
        </div>
      )}
    </div>
  );
}

function MenuItem({ onClick, children, highlight, danger }) {
  return (
    <button
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      className={`w-full text-left px-3 py-2 text-xs transition ${
        danger
          ? 'text-red-400 hover:bg-red-500/10'
          : highlight
          ? 'text-blue-300 hover:bg-blue-500/10'
          : 'text-slate-300 hover:bg-slate-700'
      }`}
    >
      {children}
    </button>
  );
}

function FullPageSpinner({ label }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900">
      <div className="text-center">
        <div className="w-12 h-12 mx-auto mb-4 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-slate-400 text-sm">{label}</p>
      </div>
    </div>
  );
}

// ─── Support-access panel (2026-08-24 role redesign) ─────────────────────────
// The super user's control over platform support access: pending requests
// need a decision; an approved window shows its expiry and can be revoked at
// any moment. Every decision is audited server-side.
function SupportAccessPanel({ showToast }) {
  const [grants, setGrants] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const res = await fetch(`${API}/api/support-access/requests`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setGrants(data.grants || []);
    } catch { /* panel is best-effort; the server is authoritative */ }
  };
  useEffect(() => { load(); }, []);

  const act = async (id, action) => {
    setBusy(id + action);
    try {
      const res = await fetch(`${API}/api/support-access/requests/${id}/${action}`, {
        method: 'POST', credentials: 'include',
      });
      const data = await res.json();
      if (data.success) {
        showToast?.('success', action === 'approve' ? 'Access window opened' : action === 'deny' ? 'Request denied' : 'Access revoked');
        load();
      } else {
        showToast?.('error', data.message || 'Action failed');
      }
    } catch (e) {
      showToast?.('error', 'Action failed');
    }
    setBusy(null);
  };

  const pending = grants.filter(g => g.status === 'pending');
  const active = grants.filter(g => g.status === 'approved' && g.expires_at && new Date(g.expires_at) > new Date());
  if (pending.length === 0 && active.length === 0) return null;

  return (
    <div className="mb-6 rounded-xl border border-amber-500/40 bg-amber-500/10 p-4">
      <h2 className="text-sm font-bold text-amber-200 mb-1">Platform support access</h2>
      <p className="text-xs text-slate-300 mb-3">
        Platform support can only open this organization's compliance apps with your approval.
        Access is time-boxed, fully audited, and you can revoke it at any moment.
      </p>
      <div className="space-y-2">
        {pending.map(g => (
          <div key={g.id} className="flex items-start justify-between gap-4 rounded-lg bg-slate-800/60 border border-slate-700 p-3">
            <div className="min-w-0">
              <p className="text-sm text-white font-semibold">{g.requested_by_name || g.requested_by_email || 'Platform support'} requests access for {g.duration_hours} hours</p>
              <p className="text-xs text-slate-400 mt-0.5">Reason: {g.reason}</p>
              <p className="text-[11px] text-slate-500 mt-0.5">Requested {new Date(g.requested_at).toLocaleString()}</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button disabled={busy === g.id + 'approve'} onClick={() => act(g.id, 'approve')}
                className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold disabled:opacity-50">Approve</button>
              <button disabled={busy === g.id + 'deny'} onClick={() => act(g.id, 'deny')}
                className="px-3 py-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-200 text-xs font-semibold disabled:opacity-50">Deny</button>
            </div>
          </div>
        ))}
        {active.map(g => (
          <div key={g.id} className="flex items-start justify-between gap-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-3">
            <div className="min-w-0">
              <p className="text-sm text-emerald-200 font-semibold">Access window active for {g.requested_by_name || 'platform support'}</p>
              <p className="text-xs text-slate-400 mt-0.5">Ends {new Date(g.expires_at).toLocaleString()} · Reason: {g.reason}</p>
            </div>
            <button disabled={busy === g.id + 'revoke'} onClick={() => act(g.id, 'revoke')}
              className="px-3 py-1.5 rounded-lg bg-red-600/80 hover:bg-red-600 text-white text-xs font-semibold flex-shrink-0 disabled:opacity-50">Revoke now</button>
          </div>
        ))}
      </div>
    </div>
  );
}
